import React from 'react';
import { NavLink } from 'react-router-dom';

// Replace with your actual import paths
import { Routes } from 'state/ducks/routes';
import { localized } from 'state/i18n';
import { IconBigSize } from 'utilities/constants';
import { hasAtLeast } from 'utilities/roles-util';
import BiButton from 'view/shared/components/buttons/bi-button/bi-button';
import ResponsiveSpacingWrapper from 'view/shared/components/responsive-wrappers/responsive-spacing-wrapper';
import { getOnlineStatusAsIconAndTextJsx } from '../../../shared/components/online-status/online-status';

import { INotificationCustomMessage, IUnitDetailsDto, RolesType } from 'api/api';
import { IdDataTuple } from 'utilities/types-util';
import { BaleSettings } from './cards/bale-settings/bale-settings';
import { DebtorDetails } from './cards/debtor-details';
import { DeviceContent } from './cards/device-content';
import { LocationCard } from './cards/location-card';
import MachineOwnerLocation from './cards/machine-owner-location';
import { NotificationSubscriptionList } from './cards/notification-subscriptions/notification-subcription-list';
import NotificationsCard from './cards/notifications';
import TechnicalDetails from './cards/technical-details';
import { WasteHandlersEmptyings } from './cards/waste-handlers-emptyings';
import { ManualItem } from './documents-helper';

export interface MachineDetailsContentProps {
	machineId: string;
	urlId: string;
	currMachine: IdDataTuple<IUnitDetailsDto, string>;
	onlineStatus: boolean;
	signalStrength?: number;
	lastOnline?: Date;
	latitude?: string;
	longitude?: string;
	isAdmin: boolean;
	isCountryAdmin: boolean;
	isSuperAdmin: boolean;
	isServiceTechnician: boolean;
	isServiceToolCode: boolean;
	userRole: RolesType;
	manualItems: ManualItem[];
	deviceTrackerId?: string;
	deviceTrackerIsActive?: boolean;
	isTrackerDeviceLoading: boolean;
	customMessages: INotificationCustomMessage[];

	// Event Handlers
	onDeletePressed: () => void;
	onServiceToolCodePressed: () => void;
	onVideoBtnPressed: (videoName: string) => void;
	onAddTrackerPressed: () => void;
	onCustomMessagePressed: () => void;
}

export const DefaultDetailsContent: React.FC<MachineDetailsContentProps> = ({
	machineId,
	urlId,
	currMachine,
	onlineStatus,
	signalStrength,
	lastOnline,
	latitude,
	longitude,
	isAdmin,
	isCountryAdmin,
	isSuperAdmin,
	isServiceTechnician,
	isServiceToolCode,
	userRole,
	manualItems,
	deviceTrackerId,
	deviceTrackerIsActive,
	isTrackerDeviceLoading,
	customMessages,
	onDeletePressed,
	onServiceToolCodePressed,
	onVideoBtnPressed,
	onAddTrackerPressed,
	onCustomMessagePressed,
}) => {
	let statusIcon = getOnlineStatusAsIconAndTextJsx(
		onlineStatus,
		IconBigSize,
		signalStrength,
		lastOnline,
		currMachine.data.hasModem
	);

	const cardAndBalerRow = (
		<div className="flex-direction-row margin-top-40 flex-space-between-row">
			<div className="flex-direction-column flex-fill-remaining-space">
				<div className="flex-direction-row flex-center-column margin-bottom-40">
					<h1 className="text-bold margin-bottom-0">{localized('MachineDetails')}</h1>
					<div className="margin-left-40 margin-right-40px">{statusIcon}</div>
					<NavLink
						className="margin-left-5px margin-right-5px"
						to={`${Routes.UnitHistory}/${encodeURIComponent(urlId)}`}
					>
						<BiButton colorTheme="org-dark-blue-with-grey-text">{localized('UnitHistory')}</BiButton>
					</NavLink>
					{currMachine.data.ownerGroupId && isCountryAdmin && (
						<div className="margin-left-5px margin-right-5px">
							<BiButton colorTheme="org-red" onClick={onDeletePressed}>
								{localized('DeleteMachine')}
							</BiButton>
						</div>
					)}
					{currMachine.data.ownerGroupId && isServiceToolCode && (
						<div className="margin-left-5px margin-right-5px">
							<BiButton colorTheme="org-primary" onClick={onServiceToolCodePressed}>
								{localized('GenerateServiceToolCode')}
							</BiButton>
						</div>
					)}
				</div>
				<div className="flex-container margin-bottom-40 flex-start-column">
					<NotificationsCard machineDetailsId={machineId} />
					<DeviceContent
						isAdmin={isAdmin}
						isCountryAdmin={isCountryAdmin}
						deviceTrackerId={deviceTrackerId}
						deviceTrackerIsActive={deviceTrackerIsActive}
						isTrackerDeviceLoading={isTrackerDeviceLoading}
						customMessages={customMessages}
						onAddTrackerPressed={onAddTrackerPressed}
						onCustomMessagePressed={onCustomMessagePressed}
					/>
				</div>
			</div>
		</div>
	);

	const { id } = currMachine.data;

	return (
		<ResponsiveSpacingWrapper>
			{cardAndBalerRow}

			<div className="flex-direction-row flex-wrap-responsive">
				<TechnicalDetails
					isAdmin={isAdmin}
					isCountryAdmin={isCountryAdmin}
					machineDetailsId={machineId}
					handleVideoBtnPressed={onVideoBtnPressed}
					manualItems={manualItems}
				/>
				<MachineOwnerLocation
					isAdmin={isAdmin}
					isSuperAdmin={isSuperAdmin}
					isCountryAdmin={isCountryAdmin}
					isServiceTechnician={isServiceTechnician}
					machineDetailsId={machineId}
					id={id}
				/>
				<LocationCard currMachine={currMachine} latitude={latitude} longitude={longitude} />
				<DebtorDetails debtor={currMachine.data.extendedDistributor} groupsId={currMachine.data.ownerGroupId} />
				{currMachine.data.isBaler && <BaleSettings currMachine={currMachine} />}

				<NotificationSubscriptionList machineDetailsId={currMachine.data.id} />
				{hasAtLeast(userRole, currMachine.data.optimizationProjectEnabledFor) && (
					<WasteHandlersEmptyings machineDetailsId={machineId} />
				)}
			</div>
		</ResponsiveSpacingWrapper>
	);
};
